import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import KanbanListContext from 'contexts/KanbanListContext'

import useSnackbar from 'hooks/useSnackbar'
import useKanbanBoard from 'hooks/useKanbanBoard'
import useKanbanFilters from 'hooks/useKanbanFilters'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

const KanbanListProvider = ({ children }) => {
  const {
    KANBAN_DEFAULT_ORDER,
    LINK_STATUS_SOURCE_REMOVED_VALUE,
    LINK_STATUS_UNLINKED_VALUE,
    VIEW_MODE_LIST,
  } = constants.activities

  const snackbar = useSnackbar()
  const { boardId } = useKanbanBoard()
  const {
    filter,
    searchInput,
    onlyMine,
    unlinkedOnly,
    setUnlinkedOnly,
    sourceRemovedOnly,
    setSourceRemovedOnly,
    viewMode,
  } = useKanbanFilters()

  const listCacheRef = useRef(new Map())
  const listFetchParamsRef = useRef({})
  const boardIdRef = useRef(null)

  const [listPage, setListPage] = useState(1)
  const [listTasks, setListTasks] = useState([])
  const [listPerPage, setListPerPage] = useState(10)
  const [hasUnlinked, setHasUnlinked] = useState(false)
  const [listLoading, setListLoading] = useState(false)
  const [listRefreshKey, setListRefreshKey] = useState(0)
  const [listTotalCount, setListTotalCount] = useState(0)
  const [listCurrentTab, setListCurrentTab] = useState(null)
  const [hasSourceRemoved, setHasSourceRemoved] = useState(false)

  const isUnlinkedView =
    unlinkedOnly || listCurrentTab === LINK_STATUS_UNLINKED_VALUE
  const isSourceRemovedView =
    sourceRemovedOnly || listCurrentTab === LINK_STATUS_SOURCE_REMOVED_VALUE

  const listFetchParams = useMemo(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filter.filters || {}).filter(
        ([, v]) => v !== '' && v !== null && v !== undefined,
      ),
    )
    if (isSourceRemovedView) {
      return {
        perPage: listPerPage,
        page: listPage,
        order: KANBAN_DEFAULT_ORDER,
        sourceRemoved: 'only',
        ...(searchInput ? { name: searchInput } : {}),
        ...(onlyMine ? { mine: true } : {}),
      }
    }
    if (isUnlinkedView) {
      return {
        perPage: listPerPage,
        page: listPage,
        order: KANBAN_DEFAULT_ORDER,
        unlinked: 'only',
        ...(searchInput ? { name: searchInput } : {}),
        ...(onlyMine ? { mine: true } : {}),
      }
    }
    const { linkStatus, ...restFilters } = cleanFilters
    const linkStatusParam =
      linkStatus === LINK_STATUS_UNLINKED_VALUE
        ? { unlinked: 'only' }
        : linkStatus === LINK_STATUS_SOURCE_REMOVED_VALUE
        ? { sourceRemoved: 'only' }
        : {}
    return {
      perPage: listPerPage,
      page: listPage,
      order: KANBAN_DEFAULT_ORDER,
      ...restFilters,
      ...linkStatusParam,
      ...(searchInput ? { name: searchInput } : {}),
      ...(listCurrentTab ? { kanbanColumnId: listCurrentTab } : {}),
      ...(onlyMine ? { mine: true } : {}),
    }
  }, [
    listPerPage,
    listPage,
    filter.filters,
    searchInput,
    listCurrentTab,
    onlyMine,
    isUnlinkedView,
    isSourceRemovedView,
  ])

  listFetchParamsRef.current = listFetchParams
  boardIdRef.current = boardId

  useEffect(() => {
    listCacheRef.current.clear()
  }, [boardId])

  useEffect(() => {
    if (viewMode !== VIEW_MODE_LIST || boardId === null) return
    const controller = new AbortController()
    service.dponet.kanbanTasks
      .get({ unlinked: 'only', perPage: 1, page: 1, signal: controller.signal })
      .then((res) => {
        const totalCount = res?.data?.meta?.totalCount || 0
        setHasUnlinked(totalCount > 0)
      })
      .catch(() => {})
    service.dponet.kanbanTasks
      .get({
        sourceRemoved: 'only',
        perPage: 1,
        page: 1,
        signal: controller.signal,
      })
      .then((res) =>
        setHasSourceRemoved((res?.data?.meta?.totalCount ?? 0) > 0),
      )
      .catch(() => {})
    return () => controller.abort()
  }, [viewMode, boardId, listTotalCount])

  useEffect(() => {
    if (viewMode !== VIEW_MODE_LIST || boardId === null) return
    const paramsKey = JSON.stringify({ ...listFetchParams, boardId })
    const cached = listCacheRef.current.get(paramsKey)
    if (cached) {
      setListTasks(cached.tasks)
      setListTotalCount(cached.totalCount)
      return
    }
    const controller = new AbortController()
    setListLoading(true)
    service.dponet.kanbanTasks
      .get({ ...listFetchParams, signal: controller.signal })
      .then((res) => {
        const tasks = res?.data?.tasks || []
        const totalCount = res?.data?.meta?.totalCount || 0
        listCacheRef.current.set(paramsKey, { tasks, totalCount })
        setListTasks(tasks)
        setListTotalCount(totalCount)
      })
      .catch(() => {})
      .finally(() => setListLoading(false))
    return () => controller.abort()
    //eslint-disable-next-line
  }, [viewMode, boardId, listFetchParams, listRefreshKey])

  const refetchList = useCallback(() => {
    listCacheRef.current.clear()
    setListRefreshKey((previousKey) => previousKey + 1)
  }, [])

  const addTaskToList = useCallback((task) => {
    setListTasks((prev) => [task, ...prev])
    setListTotalCount((prev) => prev + 1)
    // Invalida todo o cache: a task nova pode não pertencer a outros
    // filtros/páginas, então força refetch correto na próxima visita.
    listCacheRef.current.clear()
  }, [])

  const updateTaskInList = useCallback((task) => {
    setListTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)))
    listCacheRef.current.forEach((value, key) => {
      const idx = value.tasks.findIndex((t) => t.id === task.id)
      if (idx !== -1) {
        const newTasks = [...value.tasks]
        newTasks[idx] = task
        listCacheRef.current.set(key, { ...value, tasks: newTasks })
      }
    })
  }, [])

  const removeTaskFromList = useCallback((task) => {
    setListTasks((prev) => prev.filter((t) => t.id !== task.id))
    setListTotalCount((prev) => Math.max(0, prev - 1))
    listCacheRef.current.forEach((value, key) => {
      const idx = value.tasks.findIndex((t) => t.id === task.id)
      if (idx !== -1) {
        listCacheRef.current.set(key, {
          tasks: value.tasks.filter((t) => t.id !== task.id),
          totalCount: Math.max(0, value.totalCount - 1),
        })
      }
    })
  }, [])

  const handleDeleteCard = useCallback(
    async (task) => {
      try {
        await service.dponet.kanbanTasks.destroy({ taskId: task.id })
        removeTaskFromList(task)
        snackbar.open({
          message: 'Atividade excluída com sucesso!',
          variant: 'success',
        })
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      }
    },
    [snackbar, removeTaskFromList],
  )

  const handleListChangePage = useCallback((_, newPage) => {
    setListPage(newPage + 1)
  }, [])

  const handleListChangeRowsPerPage = useCallback((e) => {
    setListPerPage(parseInt(e.target.value, 10))
    setListPage(1)
  }, [])

  const handleListTabChange = useCallback(
    (tab) => {
      setListCurrentTab(tab)
      setListPage(1)
      setUnlinkedOnly(tab === LINK_STATUS_UNLINKED_VALUE)
      setSourceRemovedOnly(tab === LINK_STATUS_SOURCE_REMOVED_VALUE)
    },
    [setUnlinkedOnly, setSourceRemovedOnly],
  )

  useEffect(() => {
    if (unlinkedOnly && listCurrentTab !== LINK_STATUS_UNLINKED_VALUE) {
      setListCurrentTab(LINK_STATUS_UNLINKED_VALUE)
      setListPage(1)
    }
    if (!unlinkedOnly && listCurrentTab === LINK_STATUS_UNLINKED_VALUE) {
      setListCurrentTab(null)
      setListPage(1)
    }
  }, [unlinkedOnly, listCurrentTab])

  useEffect(() => {
    if (
      sourceRemovedOnly &&
      listCurrentTab !== LINK_STATUS_SOURCE_REMOVED_VALUE
    ) {
      setListCurrentTab(LINK_STATUS_SOURCE_REMOVED_VALUE)
      setListPage(1)
    }
    if (
      !sourceRemovedOnly &&
      listCurrentTab === LINK_STATUS_SOURCE_REMOVED_VALUE
    ) {
      setListCurrentTab(null)
      setListPage(1)
    }
  }, [sourceRemovedOnly, listCurrentTab])

  return (
    <KanbanListContext.Provider
      value={{
        listTasks,
        listTotalCount,
        listLoading,
        listPage,
        listPerPage,
        listCurrentTab,
        hasUnlinked,
        hasSourceRemoved,
        addTaskToList,
        updateTaskInList,
        removeTaskFromList,
        handleDeleteCard,
        handleListChangePage,
        handleListChangeRowsPerPage,
        handleListTabChange,
        refetchList,
      }}
    >
      {children}
    </KanbanListContext.Provider>
  )
}

export default KanbanListProvider
